import React, { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import {
  isArray,
  isBoolean,
  isNumber,
  isDate,
  isObject,
  isString,
  isNull,
  get,
} from 'lodash';

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import MyButton from '@common/components/Button/MyButton';
import SelectSourceCheckbox from '@common/components/MyCheckbox/MyCheckbox';
import GroupedParameters from '@common/components/GroupedParameters/GroupedParameters';

const createPathNameForCheckbox = (modalShowState, propKey) => {
  return modalShowState.listSource
    ? propKey // eslint-disable-line
    : modalShowState.parent
    ? modalShowState.parent + '.' + propKey // eslint-disable-line
    : propKey; // eslint-disable-line
};

const isNodeAList = (currentField, modalShowState, propKey) => {
  return isArray(currentField) && propKey === modalShowState.listSource;
};

const isNodeAnObj = (currentField, modalShowState) => {
  return (
    isObject(currentField) &&
    !isArray(currentField) &&
    modalShowState.showRootFields
  );
};

const isListSourceNotSelected = (objKeys, modalShowState) => {
  return (
    !objKeys.includes(modalShowState.listSource) &&
    !modalShowState.showRootFields
  );
};

const isArrayWithSelectedSource = (currentField, modalShowState) => {
  return (
    isArray(currentField) &&
    modalShowState.parent !== '' &&
    modalShowState.showRootFields
  );
};

const isFieldActualPlaceholder = (currentField, modalShowState) => {
  return (
    (isString(currentField) ||
      isNumber(currentField) ||
      isDate(currentField) ||
      isNull(currentField) ||
      isBoolean(currentField)) &&
    modalShowState.showRootFields
  );
};

const TextWithPlaceholdersModal = ({
  documentJson,
  showModal,
  setShowModal,
  handleInsert,
}) => {
  const [value, setValue] = useState('');

  const { t } = useTranslation();

  const getDocTypeRecursive = (providedDocument, propKey, modalShowState) => {
    let currentField = get(providedDocument, propKey);

    if (isNodeAList(currentField, modalShowState, propKey)) {
      return <div></div>;
    }

    if (isNodeAnObj(currentField, modalShowState)) {
      let validPropKey = isNaN(propKey) ? propKey : `[${propKey}]`;

      let addParent = isNaN(propKey)
        ? modalShowState.parent + '.'
        : modalShowState.parent;
      // render object with it's node
      return (
        <GroupedParameters>
          <Typography variant='subtitle2' sx={{ mb: '4px' }}>{`${t(
            'this_is_obj'
          )} ${
            modalShowState.parent ? addParent : ''
          }${validPropKey}`}</Typography>
          {getDocumentFieldsRecursive(currentField, {
            ...modalShowState,
            parent: `${modalShowState.parent ? addParent : ''}${validPropKey}`,
            showRootFields: true,
          })}
        </GroupedParameters>
      );
    }

    if (isArrayWithSelectedSource(currentField, modalShowState)) {
      // render nested list with it's node
      return <Box sx={{ m: 1 }}></Box>;
    }

    if (isFieldActualPlaceholder(currentField, modalShowState)) {
      const checkboxFieldName = createPathNameForCheckbox(
        modalShowState,
        propKey
      );

      // render checkbox for selection of placeholder
      return (
        <SelectSourceCheckbox
          label={propKey}
          name={checkboxFieldName}
          parent={modalShowState.parent}
          checked={value === checkboxFieldName}
          onChange={changeSelected}
        />
      );
    }

    return <div />;
  };

  const getDocumentFieldsRecursive = (
    providedDocument = {},
    modalShowState
  ) => {
    let objKeys = Object.keys(providedDocument);
    if (modalShowState.show) {
      if (isListSourceNotSelected(objKeys, modalShowState)) {
        return <Box>{t('list_is_not_found')}</Box>;
      }

      return objKeys.map((propKey, index) => {
        return (
          <Box key={index}>
            {getDocTypeRecursive(providedDocument, propKey, modalShowState)}
          </Box>
        );
      });
    }
  };

  const changeSelected = (data) => {
    let newValue = data.name;

    setValue(newValue);
  };

  const onOkClicked = () => {
    handleInsert(value);

    setShowModal({
      show: false,
      listSource: '',
      parent: '',
    });
  };

  const onCancel = () => {
    setShowModal({
      show: false,
      listSource: '',
      parent: '',
    });
  };

  useEffect(() => {
    if (!showModal.show) setValue('');
  }, [showModal.show]);

  return (
    <Dialog fullWidth maxWidth={'sm'} open={showModal.show}>
      <DialogTitle>{t('modal_header')}</DialogTitle>
      <DialogContent>
        {getDocumentFieldsRecursive(documentJson, showModal)}
      </DialogContent>
      <DialogActions>
        <MyButton
          startIcon={<HighlightOffIcon />}
          variant='outlined'
          onClick={onCancel}
          color='error'
        >
          {t('cancel')}
        </MyButton>
        <MyButton
          startIcon={<DoneIcon />}
          variant='outlined'
          color='primary'
          disabled={!value}
          onClick={onOkClicked}
        >
          {t('ok')}
        </MyButton>
      </DialogActions>
    </Dialog>
  );
};

export default TextWithPlaceholdersModal;
